import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import { DeleteUser, GetUsers } from "../../../api/utility/userAPI";

const ListUser = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchUsers = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetUsers();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          userName: item.userName || "",
          blocked: item.blocked || "No",
          supervisor: item.supervisor || "No",
          nature: item.nature || "",
          doctorName: item.doctorName?.name || "",
          discountAllowed: item.discountAllowed || "No",
          discountUpto: item.discountUpto ?? "",
          labRegistration: item.labRegistration || "No",
          nursingStation: item.nursingStationId?.name || "",
          permissions: item.permissions?.join(", ") || ""
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(`/utility/user/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteUser(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },
    { name: "User Name", selector: row => row.userName, sortable: true, width: "10%" },
    { name: "Blocked", selector: row => row.blocked, sortable: true, width: "10%" },
    { name: "Supervisor", selector: row => row.supervisor, width: "10%" },
    { name: "Nature", selector: row => row.nature, width: "10%" },
    { name: "Doctor", selector: row => row.doctorName, width: "10%" },
    { name: "Discount", selector: row => row.discountAllowed, width: "10%" },
    { name: "Discount Upto", selector: row => row.discountUpto, width: "10%" },
    { name: "Lab Reg.", selector: row => row.labRegistration, width: "10%" },
    { name: "Nursing Station", selector: row => row.nursingStation, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "15%"
    }
  ];

  const headers = [
    { label: "User Name", key: "userName" },
    { label: "Blocked", key: "blocked" },
    { label: "Supervisor", key: "supervisor" },
    { label: "Nature", key: "nature" },
    { label: "Doctor", key: "doctorName" },
    { label: "Discount Allowed", key: "discountAllowed" },
    { label: "Discount Upto", key: "discountUpto" },
    { label: "Lab Registration", key: "labRegistration" },
    { label: "Nursing Station", key: "nursingStation" },
    { label: "Permissions", key: "permissions" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="users.csv">
      <button className="btn btn-primary" id="export">
        Export
      </button>
      <UncontrolledTooltip target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="Users" title="List Users" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>List Users</h5>
              <Link to={`/utility/user/add`}>
                <button className="btn btn-primary" id="create">
                  Create User
                </button>
                <UncontrolledTooltip target="create">
                  Create User
                </UncontrolledTooltip>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search users..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListUser;
